import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { EmptyState } from '../../../components/ui';
import { commandDashboard } from '../commandDashboardTokens';
import type { WeeklyChartProps, WeeklyChartWeek } from '../types';

/** Round a maximum up to a 1/2/5 x 10^n step so the axis ticks read as clean numbers. */
export const niceAxisMax = (max: number): number => {
  if (max <= 1) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const norm = max / magnitude;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * magnitude;
};

/** Hover text for a bar: the value, plus how many runs fell in the week and how many failed when known. */
const describeWeek = (week: WeeklyChartWeek, unitLabel: string): string => {
  const unit = unitLabel ? ` ${unitLabel}` : '';
  const parts = [`${week.label}: ${week.value.toLocaleString()}${unit}`];
  if (week.runs != null) {
    const failed = week.failedRuns ?? 0;
    const ok = Math.max(0, week.runs - failed);
    parts.push(week.runs === 0 ? 'no runs' : `${ok} succeeded, ${failed} failed`);
  }
  return parts.join(' · ');
};

export const WeeklyBarChart: React.FC<WeeklyChartProps> = ({ title, totalLabel, note, weeks, markLowAsFailure, loading, emptyLabel, unitLabel = '' }) => {
  const max = niceAxisMax(Math.max(1, ...weeks.map((w) => w.value)));
  const ticks = [max, max / 2, 0];
  const lastIndex = weeks.length - 1;
  // Nothing to plot: no volume and no failed run to flag. Show the empty state instead of 12 stub bars.
  const isEmpty = !!emptyLabel && weeks.every((w) => w.value === 0 && !(markLowAsFailure && w.isFailure));
  // Each bar is pinned to the bottom of a track the card sizes, with the week
  // label in its own row under the track. Sizing the bar as a flex share of the
  // whole column left no room for the label, so the tallest bar overflowed the
  // card and ran into the title row.

  return (
    <Box
      sx={{
        bgcolor: commandDashboard.color.innerCard,
        border: `1px solid ${commandDashboard.color.innerCardBorder}`,
        borderRadius: `${commandDashboard.radius.innerCard}px`,
        p: '18px',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: commandDashboard.color.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
          {totalLabel}
        </Typography>
        <Typography sx={{ fontSize: '12.5px', color: commandDashboard.color.inkSubtle, ml: 'auto' }}>{note}</Typography>
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: 200, mt: '16px', borderRadius: 1 }} />
      ) : isEmpty ? (
        <Box sx={{ flex: '1 1 auto', minHeight: '200px', mt: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState size="compact" title={emptyLabel} />
        </Box>
      ) : (
        <Box sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'stretch', gap: '8px', minHeight: '200px', mt: '16px' }}>
          {/* Y axis: labels sit beside the bar track; the week-label row is mirrored below so they line up. */}
          <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column' }} aria-hidden>
            <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              {ticks.map((tick) => (
                <Typography key={tick} sx={{ fontSize: '10.5px', lineHeight: 1, color: commandDashboard.color.inkFaint, fontVariantNumeric: 'tabular-nums' }}>
                  {tick.toLocaleString()}
                </Typography>
              ))}
            </Box>
            <Box sx={{ flex: '0 0 auto', mt: '7px', height: '13px' }} />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'stretch', gap: '6px', position: 'relative' }}>
            {weeks.map((week, idx) => {
              const pct = Math.max(week.value > 0 ? 4 : 0, Math.round((week.value / max) * 100));
              const isFailure = markLowAsFailure && week.isFailure;
              // Failure coloring takes precedence over "last bar is accent" — a failed current
              // week must read as a failure, not get masked by the accent highlight.
              const fill = isFailure ? commandDashboard.color.barFailure : idx === lastIndex ? commandDashboard.color.barAccent : commandDashboard.color.barDefault;
              return (
                <Box
                  key={week.label}
                  sx={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', '&:hover .bar-value, &:focus-within .bar-value': { opacity: 1 } }}
                >
                  <Box
                    title={describeWeek(week, unitLabel)}
                    sx={{
                      flex: '1 1 auto',
                      minHeight: 0,
                      position: 'relative',
                      borderBottom: `1px solid ${commandDashboard.color.dividerLight}`,
                      backgroundImage: `linear-gradient(${commandDashboard.color.dividerLight}, ${commandDashboard.color.dividerLight}), linear-gradient(${commandDashboard.color.dividerLight}, ${commandDashboard.color.dividerLight})`,
                      backgroundSize: '100% 1px, 100% 1px',
                      backgroundPosition: 'top, center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  >
                    <Typography
                      className="bar-value"
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: `calc(${pct}% + 2px)`,
                        textAlign: 'center',
                        fontSize: '10.5px',
                        fontWeight: 600,
                        lineHeight: 1,
                        opacity: 0,
                        transition: 'opacity 120ms ease',
                        color: commandDashboard.color.inkMuted,
                        fontVariantNumeric: 'tabular-nums',
                        pointerEvents: 'none',
                      }}
                    >
                      {week.value.toLocaleString()}
                    </Typography>
                    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${pct}%`, borderRadius: commandDashboard.radius.bar, bgcolor: fill }} />
                  </Box>
                  <Typography sx={{ flex: '0 0 auto', mt: '7px', textAlign: 'center', fontSize: '10.5px', lineHeight: '13px', color: commandDashboard.color.inkFaint, whiteSpace: 'nowrap' }}>
                    {week.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
